import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_6551900b-6eea-49b4-a7ed-baa10c533f6e/artifacts/fk1eq9f3_flyoverAravellGolf.mp4';

const translations = {
  ca: { title: 'Vola sobre Aravell', button: 'Veure vídeo' },
  es: { title: 'Vuela sobre Aravell', button: 'Ver vídeo' },
  fr: { title: 'Survolez Aravell', button: 'Voir vidéo' },
  en: { title: 'Fly over Aravell', button: 'Watch video' }
};

// Floating button version for homepage
export const FloatingVideoButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed left-4 bottom-24 z-40 flex items-center gap-2 bg-[#1A4D2E] text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:bg-[#153d24] transition-all duration-300 group"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="floating-video-btn"
      >
        <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
          <Play className="w-4 h-4 fill-current" />
        </span>
        <span className="text-xs font-semibold tracking-wide uppercase hidden sm:inline">
          {t.button}
        </span>
      </motion.button>

      {/* Video Modal */}
      <VideoModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

// Section version for El Camp page
export const VideoSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-8"
      >
        <button
          onClick={() => setIsOpen(true)}
          className="group relative w-full overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
          data-testid="video-section-btn"
        >
          {/* Thumbnail with play overlay */}
          <div className="relative aspect-video bg-gradient-to-br from-[#1A4D2E] to-[#2d6b47]">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
                <rect width="100" height="100" fill="url(#grid)" />
              </svg>
            </div>
            
            {/* Play button */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.div 
                className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
                whileHover={{ scale: 1.1 }}
              >
                <Play className="w-6 h-6 sm:w-8 sm:h-8 text-[#1A4D2E] fill-current ml-1" />
              </motion.div>
              <p className="mt-4 text-white text-sm sm:text-base font-semibold tracking-wide uppercase">
                {t.title}
              </p>
              <p className="mt-1 text-white/70 text-xs tracking-wider">
                {language === 'ca' ? 'Vídeo aeri del camp' : 
                 language === 'es' ? 'Vídeo aéreo del campo' :
                 language === 'fr' ? 'Vidéo aérienne du parcours' : 
                 'Aerial video of the course'}
              </p>
            </div>
          </div>
        </button>
      </motion.div>

      {/* Video Modal */}
      <VideoModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

// Shared Video Modal
const VideoModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={onClose}
          data-testid="video-modal"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Tancar"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Title */}
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 left-4 text-white text-lg font-semibold"
          >
            {t.title}
          </motion.p>

          {/* Video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={VIDEO_URL}
              controls
              autoPlay
              className="w-full rounded-lg shadow-2xl"
              style={{ maxHeight: '80vh' }}
            >
              El teu navegador no suporta vídeos HTML5.
            </video>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingVideoButton;
